import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { FaCheckCircle, FaTimes, FaClock } from 'react-icons/fa';

export default function AdminModeration() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [pendingListings, setPendingListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchPendingListings();
    }
  }, [user]);

  const fetchPendingListings = async () => {
    try {
      const response = await axios.get(`${API}/user-listings/admin/pending?admin_id=${user.id}`);
      setPendingListings(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching pending listings:', error);
      setLoading(false);
    }
  };

  const handleModerate = async (listingId, action, notes = '') => {
    try {
      await axios.post(
        `${API}/user-listings/admin/moderate?admin_id=${user.id}`,
        {
          listing_id: listingId,
          action: action,
          admin_notes: notes
        }
      );
      
      toast.success(
        action === 'approve' 
          ? (i18n.language === 'fr' ? 'Annonce approuvée' : 'Listing approved')
          : (i18n.language === 'fr' ? 'Annonce rejetée' : 'Listing rejected')
      );
      
      fetchPendingListings();
    } catch (error) {
      console.error('Error moderating listing:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const handleApprove = (listingId) => {
    handleModerate(listingId, 'approve');
  };

  const handleReject = (listingId) => {
    const reason = prompt(
      i18n.language === 'fr' 
        ? 'Raison du rejet (optionnel):' 
        : 'Reason for rejection (optional):'
    );
    handleModerate(listingId, 'reject', reason || '');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-600">{t('loading')}</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-screen bg-[#F5F5F5]">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-8">
            <FaClock className="text-4xl text-[#00D4FF]" />
            <div>
              <h1 className="text-4xl font-bold text-[#0A2540]">
                {i18n.language === 'fr' ? 'Modération des Annonces' : 'Listing Moderation'}
              </h1>
              <p className="text-gray-600">
                {pendingListings.length} {i18n.language === 'fr' ? 'annonce(s) en attente' : 'pending listing(s)'}
              </p>
            </div>
          </div>

          {pendingListings.length === 0 ? (
            <Card className="p-12 text-center">
              <FaCheckCircle className="text-6xl text-[#635BFF] mx-auto mb-4" />
              <p className="text-gray-500 text-lg">
                {i18n.language === 'fr' 
                  ? 'Aucune annonce en attente de modération' 
                  : 'No listings pending moderation'}
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {pendingListings.map(listing => (
                <Card key={listing.id} className="border-l-4 border-orange-500">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-xl">
                            {i18n.language === 'fr' ? listing.title_fr : listing.title_en}
                          </h3>
                          <span className="px-3 py-1 bg-[#00D4FF]/10 text-orange-700 rounded-full text-sm">
                            {listing.listing_type}
                          </span>
                        </div>
                        
                        <p className="text-gray-600 mb-3">
                          {i18n.language === 'fr' ? listing.description_fr : listing.description_en}
                        </p>

                        <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                          <div>
                            <strong>{i18n.language === 'fr' ? 'Utilisateur:' : 'User:'}</strong> {listing.user_name}
                          </div>
                          <div>
                            <strong>{i18n.language === 'fr' ? 'Email:' : 'Email:'}</strong> {listing.user_email}
                          </div>
                          <div>
                            <strong>{i18n.language === 'fr' ? 'Localisation:' : 'Location:'}</strong> {listing.location || 'N/A'}
                          </div>
                          <div>
                            <strong>{i18n.language === 'fr' ? 'Prix:' : 'Price:'}</strong> {listing.price ? `${listing.price} €` : 'N/A'}
                          </div>
                          <div>
                            <strong>{i18n.language === 'fr' ? 'Date:' : 'Date:'}</strong> {new Date(listing.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2 ml-4">
                        <Button 
                          onClick={() => handleApprove(listing.id)}
                          className="bg-[#635BFF] hover:bg-[#5248d9] flex items-center gap-2"
                        >
                          <FaCheckCircle />
                          {i18n.language === 'fr' ? 'Approuver' : 'Approve'}
                        </Button>
                        <Button 
                          onClick={() => handleReject(listing.id)}
                          variant="outline"
                          className="text-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                          <FaTimes />
                          {i18n.language === 'fr' ? 'Rejeter' : 'Reject'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
